import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState(null);

  // Verify token on page load
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setMessage({ type: 'error', text: 'رابط غير صالح' });
        return;
      }

      try {
        await axios.get(`${BACKEND_URL}/api/auth/verify-reset-token/${token}`);
        setTokenValid(true);
      } catch (err) {
        setMessage({ type: 'error', text: 'انتهت صلاحية الرابط أو أنه غير صالح' });
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: 'يرجى إدخال كلمة المرور' });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'كلمتا المرور غير متطابقتين' });
      return;
    }
    
    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token,
        password: newPassword
      });
      setSuccess(true);
      setMessage({ type: 'success', text: '✅ تم تغيير كلمة المرور بنجاح!' });
      
      // Redirect to login after 3 seconds
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'حدث خطأ، حاول مرة أخرى' });
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="page-container islamic-pattern">
        <div className="card" style={{ maxWidth: '450px', width: '100%', padding: '60px 32px', textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '20px' }}>⏳</div>
          <p style={{ color: 'var(--text-light)' }}>جاري التحقق من الرابط...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid && !verifying) {
    return (
      <div className="page-container islamic-pattern">
        <div className="card" style={{ maxWidth: '450px', width: '100%', padding: '40px 32px', textAlign: 'center' }}>
          <div style={{ fontSize: '60px', marginBottom: '20px' }}>❌</div>
          <h2 style={{ color: '#D32F2F', marginBottom: '16px' }}>رابط غير صالح</h2>
          <p style={{ color: 'var(--text-light)', marginBottom: '24px', lineHeight: '1.8' }}>
            انتهت صلاحية هذا الرابط أو أنه غير صحيح.<br/>
            يرجى طلب رابط جديد.
          </p>
          <button 
            onClick={() => navigate('/forgot-password')}
            className="btn btn-primary"
            style={{ background: 'linear-gradient(135deg, #00897B, #00B4A2)' }}
          >
            طلب رابط جديد
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="page-container islamic-pattern">
        <div className="card" style={{ maxWidth: '450px', width: '100%', padding: '40px 32px', textAlign: 'center' }}>
          <div style={{ 
            fontSize: '60px', 
            marginBottom: '20px',
            animation: 'bounce 1s ease'
          }}>
            ✅
          </div>
          <h2 style={{ color: '#00897B', marginBottom: '16px' }}>تم بنجاح!</h2>
          <p style={{ color: 'var(--text-light)', marginBottom: '24px' }}>
            تم تغيير كلمة المرور بنجاح.<br/>
            جاري تحويلك لصفحة تسجيل الدخول...
          </p>
          <div style={{ 
            width: '100%', 
            height: '4px', 
            background: '#eee', 
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{ 
              width: '100%', 
              height: '100%', 
              background: 'linear-gradient(135deg, #00897B, #00B4A2)',
              animation: 'progress 3s linear'
            }}></div>
          </div>
          
          <style>{`
            @keyframes bounce {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.2); }
            }
            @keyframes progress {
              from { transform: translateX(-100%); }
              to { transform: translateX(0); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="page-container islamic-pattern">
      <div className="card" style={{ maxWidth: '450px', width: '100%', padding: '40px 32px' }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ fontSize: '50px', marginBottom: '16px' }}>🔑</div>
          <h1 style={{ 
            fontSize: '24px', 
            color: '#00897B', 
            marginBottom: '8px',
            fontFamily: "'Amiri', serif"
          }}>
            إعادة تعيين كلمة المرور
          </h1>
          <p style={{ color: 'var(--text-light)', fontSize: '14px' }}>
            أدخل كلمة المرور الجديدة
          </p>
        </div>

        {/* Message */}
        {message && (
          <div style={{
            padding: '14px 18px',
            borderRadius: '10px',
            marginBottom: '20px',
            background: message.type === 'success' ? 'rgba(0, 180, 162, 0.1)' : 'rgba(211, 47, 47, 0.1)',
            color: message.type === 'success' ? '#00897B' : '#D32F2F',
            textAlign: 'center',
            border: `1px solid ${message.type === 'success' ? 'rgba(0, 180, 162, 0.2)' : 'rgba(211, 47, 47, 0.2)'}`
          }}>
            {message.text}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="form-group password-field">
            <label className="form-label">كلمة المرور الجديدة</label>
            <div className="password-input-wrapper">
              <input
                type={showNewPassword ? 'text' : 'password'}
                className="input password-input"
                placeholder="6 أحرف على الأقل"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength="6"
                required
                data-testid="new-password-input"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowNewPassword(!showNewPassword)}
                aria-label={showNewPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
              >
                {showNewPassword ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
            </div>
          </div>
          
          <div className="form-group password-field">
            <label className="form-label">تأكيد كلمة المرور</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                className="input password-input"
                placeholder="أعد كتابة كلمة المرور"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength="6"
                required
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
              >
                {showConfirmPassword ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
            </div>
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary btn-full" 
            disabled={loading}
            data-testid="reset-password-btn"
            style={{ 
              background: 'linear-gradient(135deg, #00897B, #00B4A2)',
              fontSize: '16px',
              padding: '14px'
            }}
          >
            {loading ? '⏳ جاري الحفظ...' : '🔐 حفظ كلمة المرور الجديدة'}
          </button>
        </form>

        {/* Back to Login */}
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <button 
            onClick={() => navigate('/login')}
            style={{ 
              background: 'none', 
              border: 'none', 
              color: '#00897B', 
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            ← العودة لتسجيل الدخول
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
